import React, { useEffect, useRef, useState } from 'react';

type BasicListItemElement = React.ReactElement<{ liActive?: string }>;

type ItemProps = Readonly<{
  guidUrl?: string;
  item: BasicListItemElement;
}>;

function Item({ guidUrl, item }: ItemProps): JSX.Element {
  const [isActive, setIsActive] = useState(false);
  const itemRef = useRef<HTMLLIElement>(null);
  const isDeepLinkTarget = Boolean(guidUrl) && guidUrl === item.props.liActive;

  useEffect(() => {
    if (!isDeepLinkTarget) return undefined;

    const activateTimer = setTimeout(() => {
      setIsActive(true);
      itemRef.current?.focus();
    }, 1000);
    const deactivateTimer = setTimeout(() => setIsActive(false), 3000);

    return () => {
      clearTimeout(activateTimer);
      clearTimeout(deactivateTimer);
    };
  }, [isDeepLinkTarget]);

  return (
    <li
      aria-current={isActive ? 'true' : undefined}
      className={`basic-list__item ${isActive ? 'basic-list__item--active' : ''}`}
      ref={itemRef}
      tabIndex={-1}
    >
      {item}
    </li>
  );
}

export type BasicListProps = Readonly<{
  as?: 'ol' | 'ul';
  guidUrl?: string;
  items?: BasicListItemElement[];
  withBorder?: boolean;
}>;

function BasicList({ as: Component = 'ul', guidUrl, items = [], withBorder }: BasicListProps): JSX.Element {
  useEffect(() => {
    if (!guidUrl) return undefined;

    const element = document.getElementById(guidUrl);
    const scrollTimer = setTimeout(() => {
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 1000);

    return () => clearTimeout(scrollTimer);
  }, [guidUrl]);

  return (
    <Component className={`basic-list ${withBorder ? 'basic-list--with-border' : ''}`} role="list">
      {items.map((item, i) => (
        <Item key={item?.key ?? i} guidUrl={guidUrl} item={item} />
      ))}
    </Component>
  );
}

export default BasicList;
